import React, { useEffect, useState } from 'react';
import { departmentsApi, usersApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Plus, Edit, Trash2, Building2, Users, UserPlus, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '../../components/ui/checkbox';

const getErrorMessage = (error) => {
  return error.response?.data?.detail || error.message || 'An error occurred';
};

export const DepartmentManagementPage = () => {
  const [departments, setDepartments] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [editDept, setEditDept] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  
  // Form states - now single manager selection (not arrays)
  const [formData, setFormData] = useState({ name: '', managerId: '', additionalManagerId: '', voucherAccessEnabled: false });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [deptsData, managersData] = await Promise.all([
        departmentsApi.getAll(),
        usersApi.getManagers(),
      ]);
      setDepartments(deptsData);
      setManagers(managersData);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Department name is required');
      return;
    }
    
    setSaving(true);
    try {
      // Convert single IDs to arrays for backend compatibility
      const payload = {
        name: formData.name,
        managerIds: formData.managerId ? [formData.managerId] : [],
        additionalManagerIds: formData.additionalManagerId ? [formData.additionalManagerId] : [],
        voucherAccessEnabled: formData.voucherAccessEnabled,
      };
      const created = await departmentsApi.create(payload);
      setDepartments([...departments, created]);
      toast.success('Department created successfully');
      setCreateDialog(false);
      setFormData({ name: '', managerId: '', additionalManagerId: '', voucherAccessEnabled: false });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editDept) return;
    
    setSaving(true);
    try {
      // Convert single IDs to arrays for backend compatibility
      const payload = {
        name: editDept.name,
        managerIds: editDept.managerId ? [editDept.managerId] : [],
        additionalManagerIds: editDept.additionalManagerId ? [editDept.additionalManagerId] : [],
        voucherAccessEnabled: editDept.voucherAccessEnabled,
      };
      const updated = await departmentsApi.update(editDept.id, payload);
      setDepartments(departments.map(d => d.id === updated.id ? updated : d));
      toast.success('Department updated successfully');
      setEditDept(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  // Toggle voucher access for a department directly from table
  const handleToggleVoucherAccess = async (dept) => {
    try {
      const payload = {
        voucherAccessEnabled: !dept.voucherAccessEnabled,
      };
      const updated = await departmentsApi.update(dept.id, payload);
      setDepartments(departments.map(d => d.id === updated.id ? updated : d));
      toast.success(`Department voucher access ${updated.voucherAccessEnabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    
    try {
      await departmentsApi.delete(deleteDialog.id);
      setDepartments(departments.filter(d => d.id !== deleteDialog.id));
      toast.success('Department deleted successfully');
      setDeleteDialog(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // Single manager selection handlers
  const selectManager = (managerId, isEdit = false) => {
    if (isEdit && editDept) {
      // Toggle off if same manager clicked, otherwise set new manager
      const newValue = editDept.managerId === managerId ? '' : managerId;
      setEditDept({ ...editDept, managerId: newValue });
    } else {
      const newValue = formData.managerId === managerId ? '' : managerId;
      setFormData({ ...formData, managerId: newValue });
    }
  };

  const selectAdditionalManager = (managerId, isEdit = false) => {
    if (isEdit && editDept) {
      const newValue = editDept.additionalManagerId === managerId ? '' : managerId;
      setEditDept({ ...editDept, additionalManagerId: newValue });
    } else {
      const newValue = formData.additionalManagerId === managerId ? '' : managerId;
      setFormData({ ...formData, additionalManagerId: newValue });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="department-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight">Department Management</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage departments and assign managers
          </p>
        </div>
        <Button
          onClick={() => {
            setFormData({ name: '', managerId: '', additionalManagerId: '' });
            setCreateDialog(true);
          }}
          className="bg-[#6a5da4] hover:bg-[#5a4d94]"
          data-testid="add-department-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Department
        </Button>
      </div>

      {/* Departments Table */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#6a5da4]" /> Departments ({departments.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {departments.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No departments found. Create one to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold">Department Name</TableHead>
                  <TableHead className="font-semibold">Primary Managers</TableHead>
                  <TableHead className="font-semibold">Additional Managers</TableHead>
                  <TableHead className="font-semibold text-center">Dept. Vouchers Access</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {departments.map((dept) => (
                  <TableRow key={dept.id} data-testid={`dept-row-${dept.id}`}>
                    <TableCell className="font-medium">{dept.name}</TableCell>
                    <TableCell>
                      {dept.managers?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {dept.managers.map((m) => (
                            <span
                              key={m.id}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700"
                            >
                              {m.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">No managers assigned</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {dept.additionalManagers?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {dept.additionalManagers.map((m) => (
                            <span
                              key={m.id}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700"
                            >
                              {m.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center">
                        <Checkbox
                          checked={dept.voucherAccessEnabled || false}
                          onCheckedChange={() => handleToggleVoucherAccess(dept)}
                          data-testid={`voucher-access-${dept.id}`}
                          className="data-[state=checked]:bg-[#6a5da4] data-[state=checked]:border-[#6a5da4]"
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditDept({ 
                            ...dept, 
                            // Convert arrays to single values (take first item)
                            managerId: dept.managerIds?.[0] || '',
                            additionalManagerId: dept.additionalManagerIds?.[0] || '',
                            voucherAccessEnabled: dept.voucherAccessEnabled || false
                          })}
                          data-testid={`edit-dept-${dept.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteDialog(dept)}
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`delete-dept-${dept.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Department</DialogTitle>
            <DialogDescription>
              Add a new department and assign managers
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="dept-name">Department Name</Label>
              <Input
                id="dept-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Finance, HR, IT"
                data-testid="dept-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" /> Primary Manager (First Approval)
              </Label>
              <p className="text-xs text-muted-foreground">Select one manager for first level approval</p>
              <div className="border rounded-lg max-h-40 overflow-y-auto">
                {managers.length === 0 ? (
                  <p className="p-4 text-center text-muted-foreground text-sm">
                    No managers available. Create manager users first.
                  </p>
                ) : (
                  managers.map((manager) => (
                    <div
                      key={manager.id}
                      className={`flex items-center gap-3 p-3 hover:bg-gray-50 border-b last:border-b-0 cursor-pointer ${
                        formData.managerId === manager.id ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => selectManager(manager.id)}
                    >
                      <input
                        type="radio"
                        name="primaryManager"
                        checked={formData.managerId === manager.id}
                        onChange={() => selectManager(manager.id)}
                        className="w-4 h-4 text-blue-600"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{manager.name}</p>
                        <p className="text-xs text-muted-foreground">{manager.email}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-green-600" /> Additional Manager (Second Approval)
              </Label>
              <p className="text-xs text-muted-foreground">
                Optional: Select one manager for second level approval
              </p>
              <div className="border rounded-lg max-h-40 overflow-y-auto border-green-200">
                {managers.length === 0 ? (
                  <p className="p-4 text-center text-muted-foreground text-sm">
                    No managers available.
                  </p>
                ) : (
                  managers.map((manager) => (
                    <div
                      key={manager.id}
                      className={`flex items-center gap-3 p-3 hover:bg-green-50 border-b last:border-b-0 cursor-pointer ${
                        formData.additionalManagerId === manager.id ? 'bg-green-50' : ''
                      }`}
                      onClick={() => selectAdditionalManager(manager.id)}
                    >
                      <input
                        type="radio"
                        name="additionalManager"
                        checked={formData.additionalManagerId === manager.id}
                        onChange={() => selectAdditionalManager(manager.id)}
                        className="w-4 h-4 text-green-600"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{manager.name}</p>
                        <p className="text-xs text-muted-foreground">{manager.email}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={saving || !formData.name.trim()}
              className="bg-[#6a5da4] hover:bg-[#5a4d94]"
              data-testid="create-dept-btn"
            >
              {saving ? 'Creating...' : 'Create Department'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editDept} onOpenChange={(open) => !open && setEditDept(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Department</DialogTitle>
            <DialogDescription>
              Update department name and assigned managers
            </DialogDescription>
          </DialogHeader>
          {editDept && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-dept-name">Department Name</Label>
                <Input
                  id="edit-dept-name"
                  value={editDept.name}
                  onChange={(e) => setEditDept({ ...editDept, name: e.target.value })}
                  data-testid="edit-dept-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Users className="w-4 h-4" /> Primary Manager (First Approval)
                </Label>
                <p className="text-xs text-muted-foreground">Select one manager for first level approval</p>
                <div className="border rounded-lg max-h-40 overflow-y-auto">
                  {managers.map((manager) => (
                    <div
                      key={manager.id}
                      className={`flex items-center gap-3 p-3 hover:bg-gray-50 border-b last:border-b-0 cursor-pointer ${
                        editDept.managerId === manager.id ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => selectManager(manager.id, true)}
                    >
                      <input
                        type="radio"
                        name="editPrimaryManager"
                        checked={editDept.managerId === manager.id}
                        onChange={() => selectManager(manager.id, true)}
                        className="w-4 h-4 text-blue-600"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{manager.name}</p>
                        <p className="text-xs text-muted-foreground">{manager.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-green-600" /> Additional Manager (Second Approval)
                </Label>
                <p className="text-xs text-muted-foreground">
                  Optional: Select one manager for second level approval
                </p>
                <div className="border rounded-lg max-h-40 overflow-y-auto border-green-200">
                  {managers.map((manager) => (
                    <div
                      key={manager.id}
                      className={`flex items-center gap-3 p-3 hover:bg-green-50 border-b last:border-b-0 cursor-pointer ${
                        editDept.additionalManagerId === manager.id ? 'bg-green-50' : ''
                      }`}
                      onClick={() => selectAdditionalManager(manager.id, true)}
                    >
                      <input
                        type="radio"
                        name="editAdditionalManager"
                        checked={editDept.additionalManagerId === manager.id}
                        onChange={() => selectAdditionalManager(manager.id, true)}
                        className="w-4 h-4 text-green-600"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{manager.name}</p>
                        <p className="text-xs text-muted-foreground">{manager.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDept(null)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={saving || !editDept?.name?.trim()}
              className="bg-[#6a5da4] hover:bg-[#5a4d94]"
              data-testid="update-dept-btn"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteDialog} onOpenChange={(open) => !open && setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Department</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteDialog?.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DepartmentManagementPage;
